import { useState } from 'react';
import { twMerge, useIsClientReady } from '@belong/common';
import { IconCaretDown } from '@belong/icons';
import { ButtonBase, Text, Image } from '@belong/ui';
import clsx from 'clsx';
import { useModal } from 'src/hooks/useModal';
import { From, RegionSelect } from 'src/modals/region-select/region-select';
import { useStore } from 'src/store';
import { regionSelector } from 'src/store/region';
import css from './region-selector-button.module.css';

const commonClassName = 'uppercase transition duration-hover';

type Color = 'navy' | 'white';

type RegionSelectorButtonProps = {
  color?: Color;
  from?: From;
  theme?: 'light' | 'dark';
  className?: string;
};

export function RegionSelectorButton({
  color = 'navy',
  from = 'homeowners',
  theme = 'light',
  className,
}: RegionSelectorButtonProps) {
  const isDarkTheme = theme === 'dark';
  const [isHover, setIsHover] = useState(false);
  const isClientReady = useIsClientReady();
  const selectedRegion = useStore(regionSelector);
  const [
    isRegionSelectModalOpen,
    openRegionSelectModal,
    closeRegionSelectModal,
  ] = useModal();

  const hoverClassNameMap: Record<Color, string> = {
    navy: clsx(commonClassName, {
      [isDarkTheme ? 'text-green' : 'text-black']: isHover,
    }),
    white: twMerge(
      clsx(`text-white ${commonClassName}`, { 'text-green': isHover }),
    ),
  };

  const onDismiss = () => {
    closeRegionSelectModal();
  };

  return (
    <>
      <ButtonBase
        className={clsx(
          'flex items-center',
          theme === 'dark' ? 'text-white' : 'text-navy',
          className,
        )}
        onClick={openRegionSelectModal}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        <div className={css.flag}>
          <Image src="/icons/us_flag.svg" alt="" />
        </div>
        <div className="flex flex-1 items-center justify-between">
          <Text
            variant="p1"
            fontWeight="semibold"
            className={clsx(
              'mr-2xs whitespace-nowrap',
              hoverClassNameMap[color],
            )}
          >
            {isClientReady && selectedRegion?.region
              ? selectedRegion.region
              : 'SELECT CITY'}
          </Text>
          <IconCaretDown width={10} className={hoverClassNameMap[color]} />
        </div>
        {/* TODO: check if the 'from' attribute of the modal should be dynamic depending on the page  */}
      </ButtonBase>
      <RegionSelect
        from={from}
        isOpen={isRegionSelectModalOpen}
        onDismiss={onDismiss}
      />
    </>
  );
}
